import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';
import { useWorkListActions, useWorkListState } from '../../shared/hooks/useApi/useWorkList';
import { useWorkViewStore } from '../../shared/store/WorkViewStore';
import { WorkData } from '../../shared/dto/EntityRepository';
import { useEditMode } from '../../shared/hooks/useEditMode';
import { useBulkStore, useWorkListStore } from '../../shared/store/WorkListStore';
import { useUnsavedChanges } from '../../shared/hooks/useUnsavedChanges';
import WorkInfo from './WorkInfo';
import AttributedWorkInfo from './AttributedWorkInfo';
import EmptyWorkList from './EmptyWorkList';
import WorkViewer from './WorkViewer';
import MultiWorkViewer from './MultiWorkViewer';
import WorkViewerShell from './WorkViewerShell';
import DragDropLayer from './DragDropLayer';
import { useAuth } from '../../shared/hooks/useApi/useAuth';
import { useStandardAlertStore } from '../../shared/store/portal/alertStore';
import { getAlertMessage } from '../../shared/constants/alertMessages';
import { BULK_UPLOAD_IMAGE_MAX_COUNT } from '../../shared/constants/limits';
import { AlertPosition, AlertType, DeviceType, ModalType, WorkListType, getNextWorkListType } from '../../shared/enum/EnumRepository';
import { useModalStore } from '../../shared/store/portal/modalStore';
import HintWrapper from '../../shared/component/headless/button/HintWrapper';
import { getHintMessage } from '../../shared/constants/hintMessages';
import createIcon from '../../asset/icon/create.png';
import { useBulk } from '../../shared/hooks/useApi/useBulk';
import { useWorkDetailActions } from '../../shared/hooks/useApi/useWorkDetail';
import { getImageDimensions, removeFileExtension } from '../../shared/util/imageUtils';
import { filterSupportedImageFiles } from '../../shared/util/imageFileFormat';
import { UpdateImgReq } from '../../shared/dto/ReqDtoRepository';
import { useWorkListSelection } from '../../shared/hooks/useWorkListSelection';
import { useWorkIndexing } from '../../shared/hooks/useWorkIndexing';
import { useWorkTagStore } from '../../shared/store/workTagStore';
import { withWorkLabelFilter } from './buildWorkListParams';
import UniversalIconBtn from '../../shared/component/headless/button/UniversalIconBtn';
import { useDeviceStore } from '../../shared/store/deviceStore';
import downloadIcon from '../../asset/icon/download.png';
import gridIcon from '../../asset/icon/grid.png';
import listIcon from '../../asset/icon/list.png';
import { useWorkListMarquee } from '../../shared/hooks/useWorkListMarquee';
import reorderIcon from '../../asset/icon/reorder.png';
import IndexWorkViewer from './IndexWorkViewer';
import { usePageOwnerIdentityStore } from '../../shared/store/pageOwnerIdentityStore';
import { useParams } from 'react-router-dom';
import { isPageOwnerMatchedToRaw } from '../../shared/util/pageOwnerMatch';
import { useSettingStore } from '../../shared/store/settingStore';
import { isIndividualArtistArchive, shouldShowWorkArtist } from '../../shared/util/workArtist';
import { canShowWorkList } from '../../shared/util/visibility/workListVisibility';
import type { ResourceScope } from '../../shared/util/scopedResource';
import { useImageFileDropState } from '../../shared/hooks/useImageFileDropState';


const CREATE_WORK_INPUT_ID = 'works-create-work-input';

const WorkList: React.FC = () => {
  const setting = useSettingStore((state) => state.setting);
  const WorkInfoRow = shouldShowWorkArtist(setting) ? AttributedWorkInfo : WorkInfo;
  const { isOwner } = useAuth();
  const { isEditMode } = useEditMode();
  const { workList, workListType, listOwnerAui, listScope, status } = useWorkListState();
  const { getWorkList, getWorkListOwner, updateWorkBulk, updateWorkListType } = useWorkListActions();
  const { createWorkBulk, createDetailBulk } = useBulk();
  const { updateWorkDetailBulk } = useWorkDetailActions();
  const pageSize = useWorkListStore((s) => s.pageSize);
  const sortData = useWorkListStore((s) => s.sortData);
  const filterData = useWorkListStore((s) => s.filterData);
  const { runAfterUnsavedChangesCheck } = useUnsavedChanges();
  const { enqueueAlert } = useStandardAlertStore();
  const {
    activeDropTarget: dragState,
    handleDropTargetDragEnter,
    handleFileDragOver,
    handleFileDragLeave,
    handleImageFileDrop,
  } = useImageFileDropState<'list' | 'viewer'>();
  const workListRef = useRef<HTMLDivElement>(null);
  const { setStandardModal } = useModalStore();
  const hasMultiSelected = useWorkViewStore((s) => s.multiSelectedWork.length > 0);
  const isOverviewOpen = useWorkViewStore((s) => s.isOverviewOpen);
  const { setTotal, clearCurrent } = useBulkStore();
  const selectedTags = useWorkTagStore((s) => s.selectedTags);
  const untaggedSelected = useWorkTagStore((s) => s.untaggedSelected);
  const { isIndexingMode, setIsIndexingMode } = useWorkIndexing();
  const { deviceType } = useDeviceStore();
  const pageOwner = usePageOwnerIdentityStore(s => s.pageOwner);
  const { HANDLE: raw } = useParams<{ HANDLE?: string }>();
  const isMobile = deviceType === DeviceType.MOBILE;
  const isOverviewOpenWithIndexing = isOverviewOpen || isIndexingMode;
  const currentScope: ResourceScope = isOwner ? 'owner' : 'public';
  const canShowList = canShowWorkList({
    pageOwnerAui: pageOwner?.aui,
    listOwnerAui,
    listScope,
    currentScope,
    status,
  });
  const visibleWorkList = canShowList ? workList : [];
  const { handleWorkClick, handleKeyDown, commitMarqueeHitWorksPreview, handleWorkListBackgroundClick } =
    useWorkListSelection(visibleWorkList);

  const workListCompRef = useRef<HTMLElement>(null);

  const marqueeVisualEnabled = isEditMode && !isMobile;
  const {
    handleWorkListMouseDown,
    handleWorkListClickCapture,
    marqueeBox,
    marqueeSelecting,
  } = useWorkListMarquee({
    workList: visibleWorkList,
    workListCompRef,
    commitMarqueeHitWorksPreview,
    enabled: marqueeVisualEnabled,
  });

  const handleDownloadPDF = () => {
    setStandardModal({
      modalType: ModalType.CREATE_WORK_PDF,
      title: null,
      value: null,
      handleChange: () => {},
    });
  };

  useEffect(() => {
    if (workListRef.current) {
      workListRef.current.focus();
    }
  }, [workListRef]);

  useEffect(() => {
    const getWorkListWithApi = async () => {
      if (!isPageOwnerMatchedToRaw(raw, pageOwner)) return;
      if (isOwner) {
        await getWorkListOwner({ page: 1, size: pageSize, sortData: sortData, filterData: withWorkLabelFilter(filterData, selectedTags, untaggedSelected, isOwner) });
      } else {
        await getWorkList({ page: 1, size: pageSize, sortData: sortData, filterData: withWorkLabelFilter(filterData, selectedTags, untaggedSelected, isOwner) });
      }
    }
    getWorkListWithApi();
  }, [raw, pageOwner?.aui, pageSize, sortData, filterData, isOwner, selectedTags, untaggedSelected]);

  const openBulkCreateModal = (
    imageFiles: File[],
    target: 'list' | 'viewer',
  ) => {
    if (imageFiles.length > BULK_UPLOAD_IMAGE_MAX_COUNT) {
      enqueueAlert({
        type: AlertType.ALERT,
        position: AlertPosition.TOP,
        content: getAlertMessage('BULK_UPLOAD_IMAGE_MAX_EXCEEDED', { maxCount: BULK_UPLOAD_IMAGE_MAX_COUNT })
      });
      return;
    }
    if (imageFiles.length > 0) {
      setTotal(imageFiles.length);
      clearCurrent();
      setStandardModal({
        modalType: ModalType.BULK_UPLOAD,
        title: target === 'list' ? 'Works' : `Detail Cuts`,
        value: target,
        data: imageFiles,
        handleChange: () => { }
      });
    }
  };

  const handleCreateWork = () => {
    document.getElementById(CREATE_WORK_INPUT_ID)?.click();
  };

  const createSingleWorkFromFile = async (file: File) => {
    setTotal(1);
    clearCurrent();
    const results = await createWorkBulk([file]);
    if (!results?.length) return;
    const { width, height } = await getImageDimensions(file);
    const workListPayload: UpdateImgReq[] = [{
      id: results[0].workId,
      originUrl: results[0].originUrl,
      width,
      height,
      title: removeFileExtension(file.name)
    }];
    await updateWorkBulk({ workList: workListPayload });
    await getWorkListOwner({ page: 1, size: pageSize, sortData, filterData: withWorkLabelFilter(filterData, selectedTags, untaggedSelected, isOwner) });
  };

  const createSingleDetailFromFile = async (file: File) => {
    const activeWork = useWorkViewStore.getState().activeWork;
    if (!activeWork) return;
    const results = await createDetailBulk([file], activeWork.id);
    if (!results?.length) return;
    const { width, height } = await getImageDimensions(file);
    await updateWorkDetailBulk({
      detailList: [{
        id: results[0].workDetailId,
        originUrl: results[0].originUrl,
        width,
        height,
        description: removeFileExtension(file.name),
      }],
    });
  };

  const handleCreateWorkFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = filterSupportedImageFiles(Array.from(e.target.files ?? []));
    e.target.value = '';
    if (files.length === 0) return;

    if (files.length > BULK_UPLOAD_IMAGE_MAX_COUNT) {
      enqueueAlert({
        type: AlertType.ALERT,
        position: AlertPosition.TOP,
        content: getAlertMessage('BULK_UPLOAD_IMAGE_MAX_EXCEEDED', { maxCount: BULK_UPLOAD_IMAGE_MAX_COUNT })
      });
      return;
    }

    if (files.length === 1) {
      const [file] = files;
      void createSingleWorkFromFile(file);
      return;
    }

    setTotal(files.length);
    clearCurrent();
    setStandardModal({
      modalType: ModalType.BULK_UPLOAD,
      title: 'Works',
      value: 'list',
      data: files,
      handleChange: () => { }
    });
  };

  // WorkList 영역 드래그 핸들러
  const handleListDragEnter = (e: React.DragEvent) => {
    handleDropTargetDragEnter(e, 'list', isEditMode && !isIndexingMode);
  };

  // WorkViewer 영역 드래그 핸들러
  const handleViewerDragEnter = (e: React.DragEvent) => {
    handleDropTargetDragEnter(
      e,
      'viewer',
      isEditMode && !isIndexingMode && !hasMultiSelected,
    );
  };

  /** Files 드래그는 upload(copy), 워크 행 같은 HTML 드래그는 move 필요 (!= copy 이어야 drop 허용) */
  const handleDragOver = (e: React.DragEvent, fileDropEnabled: boolean) => {
    if (handleFileDragOver(e, fileDropEnabled)) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDroppedImages = (
    imageFiles: File[],
    target: 'list' | 'viewer',
  ) => {
    if (target === 'viewer' && !useWorkViewStore.getState().activeWork) return;
    if (imageFiles.length === 1) {
      const [file] = imageFiles;
      if (target === 'viewer') {
        void createSingleDetailFromFile(file);
      } else {
        void createSingleWorkFromFile(file);
      }
    } else {
      openBulkCreateModal(imageFiles, target);
    }
  };

  const handleDrop = (
    e: React.DragEvent,
    target: 'list' | 'viewer',
  ) => {
    const enabled = (
      isEditMode &&
      !isIndexingMode &&
      (target === 'list' || !hasMultiSelected)
    );
    handleImageFileDrop(e, target, enabled, handleDroppedImages);
  };

  // 컨테이너 전체에서 벗어날 때만 null로 초기화
  const handleContainerDragLeave = (e: React.DragEvent) => {
    handleFileDragLeave(e);
  };

  const handleReorderWorkList = () => {
    if (!isEditMode) return;
    if (isMobile) return;
    if (isIndexingMode) {
      runAfterUnsavedChangesCheck(() => setIsIndexingMode(false));
      return;
    }
    setIsIndexingMode(true);
  };

  const handleChangeWorkView = async () => {
    if (isIndexingMode) return;
    if (!isIndividualArtistArchive(setting)) return;
    await updateWorkListType({
      workListType: getNextWorkListType(workListType),
    });
  };

  return (
    <WorkListContainer
      tabIndex={0}
      ref={workListRef}
      onKeyDown={handleKeyDown}
      onDragLeave={handleContainerDragLeave}>
      <WorkListComp
        ref={workListCompRef}
        onMouseDown={handleWorkListMouseDown}
        onClickCapture={handleWorkListClickCapture}
        onClick={handleWorkListBackgroundClick}
        $marqueeSelecting={marqueeSelecting}
        $reserveOverviewSpace={isOverviewOpenWithIndexing}
        onDragOver={(e) => handleDragOver(e, isEditMode && !isIndexingMode)}
        onDrop={(e) => handleDrop(e, 'list')}
        onDragEnter={handleListDragEnter}>
        {visibleWorkList.map((each: WorkData) =>
          <WorkInfoRow
            key={each.id}
            data={each}
            handleWorkClick={handleWorkClick}
          />
        )}
        {canShowList && visibleWorkList.length === 0 && <EmptyWorkList />}
        {marqueeBox && (
          <MarqueeSelectionBox
            style={{
              left: marqueeBox.left,
              top: marqueeBox.top,
              width: marqueeBox.width,
              height: marqueeBox.height,
            }}
          />
        )}
        {isEditMode &&
          <BtnContainer data-suppress-marquee>
            {!isIndexingMode && (
              <>
                <HiddenFileInput
                  id={CREATE_WORK_INPUT_ID}
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleCreateWorkFileChange}
                />
                <HintWrapper
                  content={getHintMessage('CREATE_WORK')}
                  content2={getHintMessage('CREATE_WORK_2')}>
                  <UniversalIconBtn 
                    size="standard" 
                    border={true} 
                    backgroundImg={createIcon} 
                    onClick={handleCreateWork} />
                </HintWrapper>
                {!isMobile && isIndividualArtistArchive(setting) && (
                  <HintWrapper content={getHintMessage('CHANGE_WORK_VIEW')}>
                    <UniversalIconBtn
                      size="standard"
                      border={true}
                      backgroundImg={workListType === WorkListType.GRID ? listIcon : gridIcon}
                      onClick={handleChangeWorkView}
                    />
                  </HintWrapper>
                )}
            </>
            )}
            <HintWrapper content={getHintMessage('REORDER_WORK_LIST')}>
              <UniversalIconBtn
                size="standard"
                border={true}
                backgroundImg={reorderIcon}
                onClick={handleReorderWorkList}
              />
            </HintWrapper>
          </BtnContainer>
        }
        {(isEditMode && !isMobile) &&
          <BtnContainer2 data-suppress-marquee>
            <HintWrapper 
              content={getHintMessage('DOWNLOAD_WORK_LIST_PDF')}
              toLeft={true}>
              <UniversalIconBtn
                size="standard"
                border={false}
                backgroundImg={downloadIcon}
                onClick={handleDownloadPDF}
              />
            </HintWrapper>
          </BtnContainer2>
          }
      </WorkListComp>
      {isOverviewOpenWithIndexing && (
        <WorkViewerShell
          onDragEnter={handleViewerDragEnter}
          onDragOver={(e) => handleDragOver(
            e,
            isEditMode && !isIndexingMode && !hasMultiSelected,
          )}
          onDrop={(e) => handleDrop(e, 'viewer')}
        >
        {
          isIndexingMode ? <IndexWorkViewer /> 
          : hasMultiSelected ? <MultiWorkViewer /> 
          : <WorkViewer />
        }
        </WorkViewerShell>
      )}
      <DragDropLayer dragState={dragState} />
    </WorkListContainer>
  );
}
const WorkListContainer = styled.article`
  width: 100%;
  height: calc(80vh - 2.5vh);
  // height: calc(80vh - 2vh);
  display: flex;
  outline: none;
  position: relative;
`;

const WorkListComp = styled.section<{ $marqueeSelecting?: boolean; $reserveOverviewSpace?: boolean }>`
  flex: 1;
  width: auto;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-bottom: 10vh;
  border-bottom: 1px solid ${({ theme }) => theme.colors.color_Gray_04};
  position: relative;
  overflow-y: auto;
  user-select: ${({ $marqueeSelecting }) => ($marqueeSelecting ? 'none' : 'auto')};
  &::-webkit-scrollbar {
    display: none;
  }
  ${({ $reserveOverviewSpace }) =>
    $reserveOverviewSpace &&
    `
    [data-work-id]::after {
      content: '';
      flex: 0.15;
    }
  `}
`;

const MarqueeSelectionBox = styled.div`
  position: absolute;
  pointer-events: none;
  box-sizing: border-box;
  border: 0.5px solid ${({ theme }) => theme.colors.color_Gray_05};
  background: transparent;
  backdrop-filter: blur(1px);
  z-index: 3;
`;

const BtnContainer = styled.div`
  position: fixed;
  bottom: 8vh;
  left: 50%;
  transform: translateX(-50%);
  width: auto;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0.5vw;
  z-index: 4;
`;

const HiddenFileInput = styled.input`
  display: none;
`;

const BtnContainer2 = styled.div`
  position: fixed;
  bottom: clamp(25px, 3vw, 50px);
  right: clamp(25px, 6vw, 75px);
  width: fit-content;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: clamp(5px, 0.6vw, 15px);
  z-index: 4;
`;

export default WorkList;
